import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Text_Color = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">🎨 CSS Text Color</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>color</code> property in CSS sets the color of the text. You
        can define colors using <strong>names</strong>, <strong>HEX</strong>,
        <strong> RGB</strong>, <strong>HSL</strong>, or even with transparency
        using <strong>RGBA/HSLA</strong>.
      </p>

      {/* Section: Named Colors */}
      <div className="tutorial-section">
        <h2 className="section-title">Using Named Colors</h2>
        <p>
          CSS supports 140+ predefined color names like <code>red</code>,{" "}
          <code>blue</code>, <code>green</code>, etc.
        </p>
        <CodeCopyBlock
          code={`p {
  color: red;
}

h1 {
  color: blue;
}

span {
  color: green;
}`}
        />
        <p style={{ color: "red" }}>This text is red</p>
        <h3 style={{ color: "blue" }}>This text is blue</h3>
        <span style={{ color: "green" }}>This text is green</span>
      </div>

      {/* Section: HEX Colors */}
      <div className="tutorial-section">
        <h2 className="section-title">Using HEX Colors</h2>
        <p>
          HEX colors are written as <code>#RRGGBB</code>. Example:{" "}
          <code>#ff0000</code> is red, <code>#00ff00</code> is green.
        </p>
        <CodeCopyBlock
          code={`h2 {
  color: #ff0000; /* Red */
}

p {
  color: #00ff00; /* Green */
}

span {
  color: #0000ff; /* Blue */
}`}
        />
        <h2 style={{ color: "#ff0000" }}>This is HEX Red (#ff0000)</h2>
        <p style={{ color: "#00ff00" }}>This is HEX Green (#00ff00)</p>
        <span style={{ color: "#0000ff" }}>This is HEX Blue (#0000ff)</span>
      </div>

      {/* Section: RGB / RGBA */}
      <div className="tutorial-section">
        <h2 className="section-title">Using RGB & RGBA</h2>
        <p>
          <code>rgb(red, green, blue)</code> defines a color by mixing values
          from
          <strong>0–255</strong>.<code>rgba</code> adds an{" "}
          <strong>alpha</strong> channel for transparency.
        </p>
        <CodeCopyBlock
          code={`p {
  color: rgb(255, 165, 0); /* Orange */
}

h3 {
  color: rgba(0, 128, 0, 0.6); /* Green with 60% opacity */
}`}
        />
        <p style={{ color: "rgb(255, 165, 0)" }}>This is RGB Orange</p>
        <h3 style={{ color: "rgba(0, 128, 0, 0.6)" }}>
          This is semi-transparent Green (RGBA)
        </h3>
      </div>

      {/* Section: HSL / HSLA */}
      <div className="tutorial-section">
        <h2 className="section-title">Using HSL & HSLA</h2>
        <p>
          <code>hsl(hue, saturation, lightness)</code> gives intuitive control.
          <code>hsla</code> includes transparency.
        </p>
        <CodeCopyBlock
          code={`h2 {
  color: hsl(200, 100%, 50%); /* Bright Blue */
}

p {
  color: hsla(340, 100%, 50%, 0.7); /* Pink with transparency */
}`}
        />
        <h2 style={{ color: "hsl(200, 100%, 50%)" }}>This is HSL Blue</h2>
        <p style={{ color: "hsla(340, 100%, 50%, 0.7)" }}>
          This is semi-transparent Pink (HSLA)
        </p>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you know how to use text colors, you can explore{" "}
          <a href="/css/backgrounds" className="learn-more">
            CSS Backgrounds
          </a>{" "}
          to combine text color with background styling for even better design.
        </p>
      </div>
    </div>
  );
};

export default Text_Color;
